import { useRef, useState } from "react";
import { toast } from "sonner";
import { GitCompareArrows, Upload, Loader2, ArrowRight, Plus, Minus, Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { diffCsvs } from "@/lib/api";
import { cn } from "@/lib/utils";

const FileSlot = ({ label, testId, file, onFile }) => {
  const inputRef = useRef(null);
  return (
    <div
      className="border border-dashed border-[#1e293b] hover:border-blue-500/50 rounded-sm p-6 bg-[#0f172a]/60 cursor-pointer"
      style={{ transition: "border-color 0.15s ease" }}
      onClick={() => inputRef.current?.click()}
      data-testid={testId}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
        }}
      />
      <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-slate-400 mb-4">
        {label}
      </div>
      {file ? (
        <div className="flex items-center gap-3">
          <Upload className="h-4 w-4 text-blue-400" />
          <span className="font-mono text-sm text-slate-100 truncate">{file.name}</span>
        </div>
      ) : (
        <div className="text-sm text-slate-500 font-mono">Click to choose a .csv file</div>
      )}
    </div>
  );
};

const ChangesTable = ({ items, kind }) => {
  if (!items.length) {
    return (
      <div className="border border-[#1e293b] rounded-sm p-8 text-center text-slate-500 font-mono text-sm">
        No {kind} rules.
      </div>
    );
  }
  return (
    <div className="border border-[#1e293b] rounded-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#0f172a]">
          <tr>
            {kind === "changed" ? (
              <>
                <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono">
                  Rule ID
                </th>
                <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono">
                  Field
                </th>
                <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono">
                  Old
                </th>
                <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono"></th>
                <th className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono">
                  New
                </th>
              </>
            ) : (
              <>
                {["Rule ID", "Name", "Action", "Source", "Destination", "Services / Port"].map((h) => (
                  <th
                    key={h}
                    className="text-left px-3 py-2 text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono"
                  >
                    {h}
                  </th>
                ))}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {kind === "changed"
            ? items.flatMap((c) =>
                Object.entries(c.changes).map(([field, val], idx) => (
                  <tr
                    key={`${c.rule_id}-${field}`}
                    className="border-t border-[#1e293b]"
                    data-testid={`diff-changed-${c.rule_id}-${field}`}
                  >
                    <td className="px-3 py-2 font-mono text-slate-100">
                      {idx === 0 ? c.rule_id : ""}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-400 uppercase tracking-[0.15em]">
                      {field}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-red-300 line-through decoration-red-500/40">
                      {val.old || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <ArrowRight className="h-3 w-3 text-slate-600" />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-emerald-300">
                      {val.new || "—"}
                    </td>
                  </tr>
                ))
              )
            : items.map((r) => (
                <tr
                  key={r.rule_id}
                  className="border-t border-[#1e293b]"
                  data-testid={`diff-${kind}-${r.rule_id}`}
                >
                  <td className="px-3 py-2 font-mono text-slate-100">{r.rule_id}</td>
                  <td className="px-3 py-2 text-slate-300">{r.name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{(r.action || "").toUpperCase()}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-300">{r.source}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-300">{r.destination}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-300">
                    {r.services || `${r.protocol || ""}/${r.port || ""}`}
                  </td>
                </tr>
              ))}
        </tbody>
      </table>
    </div>
  );
};

export default function Diff() {
  const [oldFile, setOldFile] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("changed");

  const run = async () => {
    if (!oldFile || !newFile) {
      toast.error("Please select both files");
      return;
    }
    setLoading(true);
    try {
      const data = await diffCsvs(oldFile, newFile);
      setResult(data);
      toast.success(
        `Diff · +${data.summary.added} -${data.summary.removed} ~${data.summary.changed}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diff failed");
    } finally {
      setLoading(false);
    }
  };

  const stat = (val, tone) => (
    <span className={cn("font-mono text-2xl font-semibold tabular-nums", tone)}>{val}</span>
  );

  return (
    <div className="relative">
      <div className="grid-bg absolute inset-0 pointer-events-none" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <header className="pb-8 border-b border-[#1e293b]">
          <div className="flex items-center gap-2 text-blue-400 font-mono text-xs tracking-[0.3em] uppercase mb-3">
            <GitCompareArrows className="h-3.5 w-3.5" />
            <span>ruleset_diff</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-50">
            Ruleset <span className="text-blue-400">Diff</span>
          </h1>
          <p className="mt-4 text-slate-400 text-sm max-w-2xl">
            Upload two firewall policy CSVs. See exactly which rules were{" "}
            <span className="text-emerald-400 font-mono">added</span>,{" "}
            <span className="text-red-400 font-mono">removed</span>, or{" "}
            <span className="text-blue-400 font-mono">changed</span> between versions — matched by
            rule_id.
          </p>
        </header>

        <section className="pt-8 grid md:grid-cols-2 gap-4">
          <FileSlot label="Old / baseline CSV" testId="diff-old-slot" file={oldFile} onFile={setOldFile} />
          <FileSlot label="New / candidate CSV" testId="diff-new-slot" file={newFile} onFile={setNewFile} />
        </section>

        <div className="mt-6 flex items-center gap-3">
          <Button
            data-testid="run-diff-btn"
            onClick={run}
            disabled={loading || !oldFile || !newFile}
            className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GitCompareArrows className="h-4 w-4 mr-2" />}
            Compute diff
          </Button>
          {result && (
            <Button
              variant="ghost"
              onClick={() => {
                setResult(null);
                setOldFile(null);
                setNewFile(null);
              }}
              className="rounded-sm text-slate-400 hover:text-slate-100 hover:bg-[#1e293b]"
            >
              Reset
            </Button>
          )}
        </div>

        {result && (
          <section className="mt-8 space-y-6 animate-fade-up">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono mb-3">Old total</div>
                {stat(result.summary.old_total, "text-slate-200")}
              </div>
              <div className="bg-[#0f172a] border border-[#1e293b] rounded-sm p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-mono mb-3">New total</div>
                {stat(result.summary.new_total, "text-slate-200")}
              </div>
              <div className="bg-[#0f172a] border border-emerald-500/30 rounded-sm p-5" data-testid="diff-added-count">
                <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-400 font-mono mb-3 flex items-center gap-2">
                  <Plus className="h-3 w-3" /> Added
                </div>
                {stat(result.summary.added, "text-emerald-400")}
              </div>
              <div className="bg-[#0f172a] border border-red-500/30 rounded-sm p-5" data-testid="diff-removed-count">
                <div className="text-[10px] tracking-[0.2em] uppercase text-red-400 font-mono mb-3 flex items-center gap-2">
                  <Minus className="h-3 w-3" /> Removed
                </div>
                {stat(result.summary.removed, "text-red-400")}
              </div>
              <div className="bg-[#0f172a] border border-blue-500/30 rounded-sm p-5" data-testid="diff-changed-count">
                <div className="text-[10px] tracking-[0.2em] uppercase text-blue-400 font-mono mb-3 flex items-center gap-2">
                  <Edit3 className="h-3 w-3" /> Changed
                </div>
                {stat(result.summary.changed, "text-blue-400")}
              </div>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="bg-[#0f172a] border border-[#1e293b] rounded-sm">
                <TabsTrigger value="changed" data-testid="diff-tab-changed" className="rounded-sm font-mono text-xs tracking-[0.15em] uppercase">
                  Changed ({result.summary.changed})
                </TabsTrigger>
                <TabsTrigger value="added" data-testid="diff-tab-added" className="rounded-sm font-mono text-xs tracking-[0.15em] uppercase">
                  Added ({result.summary.added})
                </TabsTrigger>
                <TabsTrigger value="removed" data-testid="diff-tab-removed" className="rounded-sm font-mono text-xs tracking-[0.15em] uppercase">
                  Removed ({result.summary.removed})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="changed" className="mt-4">
                <ChangesTable items={result.changed} kind="changed" />
              </TabsContent>
              <TabsContent value="added" className="mt-4">
                <ChangesTable items={result.added} kind="added" />
              </TabsContent>
              <TabsContent value="removed" className="mt-4">
                <ChangesTable items={result.removed} kind="removed" />
              </TabsContent>
            </Tabs>
          </section>
        )}
      </div>
    </div>
  );
}
